import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 50vh;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    height: 80vh;
  }
`;

const LeftPanel = styled.div`
  width: 50%;
  background: url(${(props) => props.backgroundImage}) no-repeat center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    flex: 1;
  }
`;

const RightPanel = styled.div`
  width: 50%;
  height: 50vh;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    flex: 1;
  }
`;

const Overlay = styled.div`
  background: rgba(0, 0, 0, 0.5);
  padding: 20px;
  border-radius: 10px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Description = styled.p`
  max-height: 150px;
  overflow-y: auto;
  text-align: left;
  margin: 0;
`;

const StyledScrollbar = styled.div`
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #555;
  }
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 24px;
  font-weight: bold;
`;

const DestinationCard = ({ destinationId }) => {
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true); // New loading state
  const [error, setError] = useState(false); // New error state to handle no destination

  useEffect(() => {
    const fetchDestination = async () => {
      try {
        setLoading(true); // Set loading to true when starting the fetch
        const response = await fetch(`${API_URL}/web/destinations/view/?destid=${destinationId}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
          }
        });
        const result = await response.json();

        if (result.header.resCode === 200 && result.data.length > 0) {
          setDestination(result.data[0]); // Set destination if data is valid
        } else {
          setError(true); // Set error if no data is returned
        }
      } catch (error) {
        console.error('Error fetching destination data:', error);
        setError(true); // Set error on fetch failure
      } finally {
        setLoading(false); // Always set loading to false when fetch completes
      }
    };

    fetchDestination();
  }, [destinationId]);

  if (loading) {
    return <Loading>Loading...</Loading>; // Show loading indicator while fetching
  }

  if (error || !destination) {
    return <Loading>There is no such destination</Loading>; // Show message only if no destination or error
  }

  return (
    <Container>
      <LeftPanel backgroundImage={destination.image}>
        <Overlay as={StyledScrollbar}>
          <h1>{destination.name}</h1>
          <Description>{destination.description}</Description>
          <span>{destination.location}</span>
        </Overlay>
      </LeftPanel>
      <RightPanel>
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_PUBLIC_KEY}>
          <GoogleMap
            mapContainerStyle={{ height: "100%", width: "100%" }}
            center={{ lat: destination.latitude, lng: destination.longitude }}
            zoom={12}
          >
            <Marker position={{ lat: destination.latitude, lng: destination.longitude }} />
          </GoogleMap>
        </LoadScript>
      </RightPanel>
    </Container>
  );
};

export default DestinationCard;